import { ElementType, forwardRef, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

const typography = tv({
  variants: {
    variant: {
      "heading-1": "heading-1",
      "heading-2": "heading-2",
      "heading-3": "heading-3",
      "heading-4": "heading-4",
      "heading-5": "heading-5",
      "paragraph-xl-bold": "paragraph-xl-bold",
      "paragraph-xl-semibold": "paragraph-xl-semibold",
      "paragraph-xl-medium": "paragraph-xl-medium",
      "paragraph-xl-regular": "paragraph-xl-regular",
      "paragraph-l-bold": "paragraph-l-bold",
      "paragraph-l-semibold": "paragraph-l-semibold",
      "paragraph-l-medium": "paragraph-l-medium",
      "paragraph-l-regular": "paragraph-l-regular",
      "paragraph-m-bold": "paragraph-m-bold",
      "paragraph-m-semibold": "paragraph-m-semibold",
      "paragraph-m-medium": "paragraph-m-medium",
      "paragraph-m-regular": "paragraph-m-regular",
    },
  },
  defaultVariants: {
    variant: "paragraph-l-regular",
  },
});

type TypographyVariantProps = VariantProps<typeof typography>;

const tagMapping: Record<
  NonNullable<TypographyVariantProps["variant"]>,
  ElementType
> = {
  "heading-1": "h1",
  "heading-2": "h2",
  "heading-3": "h3",
  "heading-4": "h4",
  "heading-5": "h5",
  "paragraph-xl-bold": "p",
  "paragraph-xl-semibold": "p",
  "paragraph-xl-medium": "p",
  "paragraph-xl-regular": "p",
  "paragraph-l-bold": "p",
  "paragraph-l-semibold": "p",
  "paragraph-l-medium": "p",
  "paragraph-l-regular": "p",
  "paragraph-m-bold": "p",
  "paragraph-m-semibold": "p",
  "paragraph-m-medium": "p",
  "paragraph-m-regular": "span",
};

interface TypographyProps extends TypographyVariantProps {
  as?: ElementType;
  children: ReactNode;
  className?: string;
}

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ as, variant, className, ...props }, ref) => {
    const Tag = as || (variant ? tagMapping[variant] : "span");
    return (
      <Tag
        ref={ref}
        className={typography({ variant, className })}
        {...props}
      />
    );
  },
);

Typography.displayName = "Typography";
